// client/src/views/Interactive_parts/OverlayAssetModal.js
import React, { useEffect, useState } from 'react';
import { X, UploadCloud, Image as ImageIcon, Lock, LockOpen, Trash2 } from 'lucide-react';
import { resolveUploadsUrl } from './InteractiveMapInternals.js';

const TABLETOP_BASE = '/api/tabletop';

function markOverlayMeta(existingMeta) {
  try {
    const base = existingMeta ? JSON.parse(existingMeta) : {};
    return JSON.stringify({ ...base, kind: 'map_overlay' });
  } catch (e) {
    return JSON.stringify({ kind: 'map_overlay' });
  }
}

function OverlayAssetModal({
  mapId,
  initialSelectedAsset = null,
  onClose,
  onSelectAsset,
  onStartEdit, // (asset, locked) => void
  onDeleteAsset, // (asset) => void
}) {
  const [activeTab, setActiveTab] = useState('existing'); // 'existing' | 'upload' | 'edit'
  const [assets, setAssets] = useState([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [assetsError, setAssetsError] = useState('');
  const [deleteError, setDeleteError] = useState('');

  const [selectedId, setSelectedId] = useState(
    initialSelectedAsset && initialSelectedAsset.id != null
      ? String(initialSelectedAsset.id)
      : null
  );

  const [uploadFile, setUploadFile] = useState(null);
  const [uploadLabel, setUploadLabel] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const [lockEdit, setLockEdit] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function loadAssets() {
      if (!mapId) return;
      setLoadingAssets(true);
      setAssetsError('');
      setDeleteError('');
      try {
        const res = await fetch(
          `${TABLETOP_BASE}/maps/${mapId}/assets?type=image`,
          { credentials: 'include' }
        );
        const body = await res.json().catch(() => null);
        if (!res.ok) {
          const msg =
            (body && (body.error || body.message)) ||
            `${res.status} ${res.statusText}`;
          throw new Error(msg);
        }
        if (cancelled) return;
        const list = Array.isArray(body) ? body : body.assets || [];
        setAssets(list);
        if (!selectedId && initialSelectedAsset && initialSelectedAsset.id) {
          setSelectedId(String(initialSelectedAsset.id));
        }
      } catch (err) {
        if (!cancelled) {
          setAssetsError(err.message || 'Failed to load overlays');
        }
      } finally {
        if (!cancelled) setLoadingAssets(false);
      }
    }

    loadAssets();
    return () => { cancelled = true; };
  }, [mapId, initialSelectedAsset, selectedId]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function handleBackdropClick(e) { e.stopPropagation(); onClose(); }
  function stopPropagation(e) { e.stopPropagation(); }

  function isNumericId(v) {
    return v != null && /^\d+$/.test(String(v).trim());
  }

  function getAssetLabel(asset) {
    return (
      asset.label ||
      asset.title ||
      asset.name ||
      asset.original_filename ||
      asset.filename ||
      `Overlay #${asset.id != null ? asset.id : ''}`
    );
  }

  function getAssetUrl(asset) {
    const raw =
      asset.url ||
      asset.image_url ||
      asset.path ||
      asset.file_path ||
      asset.filepath;
    return raw ? resolveUploadsUrl(raw) : null;
  }

  // Create a numeric alias row for a legacy/UUID asset row so the rest of the app can save numeric overlay_id.
  async function createNumericAliasFromLegacy(asset) {
    // Try to reuse as much metadata as possible so the alias points to the same underlying file.
    const payload = {
      map_id: Number(mapId),
      type: asset.type || 'image',
      name: asset.name || asset.title || asset.original_filename || getAssetLabel(asset),
      title: asset.title || asset.name || getAssetLabel(asset),
      description: asset.description || null,
      // Prefer raw DB-path/url fields if present; the backend will accept either/both.
      url: asset.url || null,
      file_path: asset.file_path || null,
      stored_filename: asset.stored_filename || null,
      original_filename: asset.original_filename || null,
      mime_type: asset.mime_type || null,
      size_bytes: asset.size_bytes || asset.bytes || null,
      width: asset.width || asset.width_px || null,
      height: asset.height || asset.height_px || null,
      opacity: asset.opacity != null ? asset.opacity : 1,
      visible: asset.visible != null ? !!asset.visible : 1,
      z_index: asset.z_index != null ? asset.z_index : 0,
      anchor_x: asset.anchor_x != null ? asset.anchor_x : 0.5,
      anchor_y: asset.anchor_y != null ? asset.anchor_y : 0.5,
      scale_x: asset.scale_x != null ? asset.scale_x : 1,
      scale_y: asset.scale_y != null ? asset.scale_y : 1,
      rotation: asset.rotation != null ? asset.rotation : 0,
      rotation_deg: asset.rotation_deg != null ? asset.rotation_deg : 0,
      tags: asset.tags || '[]',
      meta: markOverlayMeta(asset.meta),
      visibility: asset.visibility || 'private',
      storage: asset.storage || (asset.url ? 'local' : 'external'),
    };

    const res = await fetch(`${TABLETOP_BASE}/assets`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const body = await res.json().catch(() => null);
    if (!res.ok) {
      const msg = (body && (body.error || body.message)) || `${res.status} ${res.statusText}`;
      throw new Error(msg);
    }
    return body; // should contain numeric id
  }

  async function handleUseSelected() {
    const asset = assets.find(a => String(a.id) === String(selectedId)) || null;
    if (!asset) {
      onClose();
      return;
    }

    let finalAsset = asset;
    if (!isNumericId(asset.id)) {
      try {
        const alias = await createNumericAliasFromLegacy(asset);
        if (alias && alias.id != null) {
          finalAsset = alias;
          setAssets(prev => [...prev, alias]);
          setSelectedId(String(alias.id));
        }
      } catch (e) {
        // If alias creation fails, we still pass the legacy asset back (so UI stays usable),
        // but downstream saving logic should ignore non-numeric ids (will result in no overlay lock).
        console.error('Failed to create numeric alias for legacy asset:', e);
      }
    }

    if (typeof onSelectAsset === 'function') {
      // Pass the full asset object back so InteractiveMap can use its metadata.
      onSelectAsset(finalAsset);
    }

    onClose();
  }

  async function handleDeleteAsset(asset) {
    if (!asset || asset.id == null) return;
    const idStr = String(asset.id);

    const confirmed = window.confirm(
      'Delete this overlay image permanently? This cannot be undone and may break any maps using it.'
    );
    if (!confirmed) return;

    setDeleteError('');
    try {
      const res = await fetch(
        `${TABLETOP_BASE}/assets/${encodeURIComponent(idStr)}?overlay=1`,
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );
      const body = await res.json().catch(() => null);
      if (!res.ok) {
        const msg =
          (body && (body.error || body.message)) ||
          `${res.status} ${res.statusText}`;
        throw new Error(msg);
      }

      setAssets(prev =>
        prev.filter(a => String(a.id) !== idStr)
      );

      if (selectedId && String(selectedId) === idStr) {
        setSelectedId(null);
      }

      if (typeof onDeleteAsset === 'function') {
        onDeleteAsset(asset);
      }
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete overlay');
    }
  }

  async function handleUploadSubmit(e) {
    e.preventDefault();
    if (!uploadFile) {
      setUploadError('Please choose an image file.');
      return;
    }
    if (!mapId) {
      setUploadError('Missing map id.');
      return;
    }
    setUploadError('');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('map_id', String(mapId));
      fd.append('file', uploadFile);
      if (uploadLabel && uploadLabel.trim()) fd.append('name', uploadLabel.trim());
      fd.append('asset_type', 'image');
      fd.append('type', 'image');
      fd.append('opacity', String(1));
      fd.append('z_index', String(0));
      fd.append('meta', JSON.stringify({ kind: 'map_overlay' }));

      const res = await fetch(`${TABLETOP_BASE}/assets/upload`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) {
        const msg = (body && (body.error || body.message)) || `${res.status} ${res.statusText}`;
        throw new Error(msg);
      }
      const saved = body || null;
      setAssets(prev => (saved ? [...prev, saved] : prev));
      if (saved && saved.id != null) setSelectedId(String(saved.id));
      setActiveTab('existing');
      setUploadFile(null);
      setUploadLabel('');
    } catch (err) {
      setUploadError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  function handleFileChange(e) {
    const file = e.target.files && e.target.files[0];
    setUploadFile(file || null);
  }

  function selectedAsset() {
    return assets.find(a => String(a.id) === String(selectedId)) || null;
  }

  return (
    <div
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        zIndex: 3000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        onClick={stopPropagation}
        style={{
          width: '90%',
          maxWidth: 780,
          maxHeight: '80vh',
          background: '#15161f',
          borderRadius: 16,
          boxShadow: '0 18px 40px rgba(0,0,0,0.7)',
          border: '1px solid #323548',
          padding: 18,
          color: '#f5f5f5',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ImageIcon size={20} />
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Map Image Overlays</h2>
          </div>
          <button
            onClick={onClose}
            style={{
              marginLeft: 'auto',
              border: 'none',
              background: 'transparent',
              color: '#bbb',
              cursor: 'pointer',
              borderRadius: 999,
              padding: 4,
            }}
            title="Close"
          >
            <X size={18} />
          </button>
        </div>

        <p style={{ marginTop: 0, marginBottom: 12, fontSize: 13, color: '#bfc2d8' }}>
          Upload a new overlay image (WebP recommended) or pick one you&apos;ve already added for this map.
          Use the Edit tab to move/resize the selected overlay directly on the map.
        </p>

        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <button
            type="button"
            onClick={() => setActiveTab('existing')}
            style={{
              flex: 1,
              padding: '6px 10px',
              borderRadius: 999,
              border: '1px solid #3c3f52',
              background: activeTab === 'existing' ? 'linear-gradient(135deg, #3c6df6, #5f9dfd)' : '#1d1f2a',
              color: activeTab === 'existing' ? '#fff' : '#c2c7dd',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Choose existing
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('upload')}
            style={{
              flex: 1,
              padding: '6px 10px',
              borderRadius: 999,
              border: '1px solid #3c3f52',
              background: activeTab === 'upload' ? 'linear-gradient(135deg, #3c6df6, #5f9dfd)' : '#1d1f2a',
              color: activeTab === 'upload' ? '#fff' : '#c2c7dd',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Upload new
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('edit')}
            style={{
              flex: 1,
              padding: '6px 10px',
              borderRadius: 999,
              border: '1px solid #3c3f52',
              background: activeTab === 'edit' ? 'linear-gradient(135deg, #3c6df6, #5f9dfd)' : '#1d1f2a',
              color: activeTab === 'edit' ? '#fff' : '#c2c7dd',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Edit
          </button>
        </div>

        <div
          style={{
            flex: 1,
            minHeight: 0,
            borderRadius: 12,
            border: '1px solid #2b2e3e',
            background: '#11121a',
            padding: 12,
            overflowY: 'auto',
          }}
        >
          {activeTab === 'existing' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {loadingAssets && (
                <div style={{ fontSize: 13, color: '#c2c7dd' }}>Loading overlays…</div>
              )}
              {assetsError && (
                <div style={{ fontSize: 12, color: '#ff7b7b', marginBottom: 6 }}>{assetsError}</div>
              )}
              {deleteError && (
                <div style={{ fontSize: 12, color: '#ffb37b', marginBottom: 6 }}>{deleteError}</div>
              )}
              {!loadingAssets && !assetsError && assets.length === 0 && (
                <div style={{ fontSize: 13, color: '#9ea3c0' }}>
                  No image assets found for this map yet. Switch to &quot;Upload new&quot; to add one.
                </div>
              )}
              {assets.map(asset => {
                const idStr = asset.id != null ? String(asset.id) : undefined;
                const url = getAssetUrl(asset);
                const isSelected = selectedId != null && idStr === selectedId;
                return (
                  <div
                    key={idStr || getAssetLabel(asset)}
                    onClick={() => idStr && setSelectedId(idStr)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      borderRadius: 10,
                      border: isSelected ? '1px solid #4f7bff' : '1px solid #242638',
                      background: isSelected ? 'rgba(79, 123, 255, 0.12)' : '#161724',
                      padding: 8,
                      cursor: 'pointer',
                      gap: 10,
                    }}
                  >
                    {url ? (
                      <div
                        style={{
                          width: 64,
                          height: 40,
                          borderRadius: 6,
                          overflow: 'hidden',
                          flexShrink: 0,
                          border: '1px solid #2c3040',
                          background: '#05060b',
                        }}
                      >
                        <img
                          src={url}
                          alt={getAssetLabel(asset)}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                      </div>
                    ) : (
                      <div
                        style={{
                          width: 64,
                          height: 40,
                          borderRadius: 6,
                          flexShrink: 0,
                          border: '1px solid #2c3040',
                          background: 'linear-gradient(135deg, #262a3e, #1a1d2b)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <ImageIcon size={18} />
                      </div>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: '#f5f5ff',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {getAssetLabel(asset)}
                      </span>
                      <span style={{ fontSize: 11, color: '#9ea3c0', marginTop: 2 }}>
                        ID: {asset.id != null ? asset.id : '—'}
                        {!isNumericId(asset.id) && (
                          <em style={{ marginLeft: 8, fontStyle: 'normal', color: '#ffdda6' }}>
                            (legacy)
                          </em>
                        )}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteAsset(asset);
                      }}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        padding: 4,
                        borderRadius: 999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#ff8585',
                      }}
                      title="Delete overlay"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {activeTab === 'upload' && (
            <form onSubmit={handleUploadSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#d1d5f0' }}>Overlay image file</label>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: 10,
                    borderRadius: 10,
                    border: '1px dashed #3a3d52',
                    background: 'radial-gradient(circle at top, #232536 0, #11121a 60%)',
                  }}
                >
                  <UploadCloud size={20} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, color: '#f5f5ff', marginBottom: 2 }}>
                      {uploadFile ? uploadFile.name : 'Choose an image'}
                    </div>
                    <div style={{ fontSize: 11, color: '#a1a6c4' }}>
                      WebP recommended (.webp). You can manually select other image types via your file picker.
                    </div>
                  </div>
                  <input
                    type="file"
                    accept="image/webp"
                    onChange={handleFileChange}
                    style={{ fontSize: 11, maxWidth: 210 }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#d1d5f0' }}>Label (optional)</label>
                <input
                  type="text"
                  value={uploadLabel}
                  onChange={e => setUploadLabel(e.target.value)}
                  placeholder="e.g. Engineering Deck layout"
                  style={{
                    padding: '6px 8px',
                    borderRadius: 8,
                    border: '1px solid #30344a',
                    background: '#0f1018',
                    color: '#f5f5ff',
                    fontSize: 13,
                    outline: 'none',
                  }}
                />
              </div>

              {uploadError && <div style={{ fontSize: 12, color: '#ff7b7b' }}>{uploadError}</div>}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
                <button
                  type="button"
                  onClick={onClose}
                  style={{
                    padding: '6px 12px',
                    borderRadius: 999,
                    border: '1px solid #3a3d52',
                    background: '#191b26',
                    color: '#d0d3ea',
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  style={{
                    padding: '6px 16px',
                    borderRadius: 999,
                    border: 'none',
                    background: uploading
                      ? 'linear-gradient(135deg, #3f4d82, #58648f)'
                      : 'linear-gradient(135deg, #3c6df6, #5f9dfd)',
                    color: '#fff',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: uploading ? 'default' : 'pointer',
                    opacity: uploadFile ? 1 : 0.85,
                  }}
                >
                  {uploading ? 'Uploading…' : 'Upload overlay'}
                </button>
              </div>
            </form>
          )}

          {activeTab === 'edit' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {(!selectedId || !selectedAsset()) && (
                <div style={{ fontSize: 13, color: '#c2c7dd' }}>
                  Select an overlay in &quot;Choose existing&quot; first, then return here to edit its placement.
                </div>
              )}
              {!!selectedId && !!selectedAsset() && (
                <>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: 10,
                      borderRadius: 10,
                      border: '1px solid #2b2e3e',
                      background: '#0f1018',
                    }}
                  >
                    <div
                      style={{
                        width: 96,
                        height: 60,
                        borderRadius: 6,
                        overflow: 'hidden',
                        border: '1px solid #2c3040',
                        background: '#05060b',
                        flexShrink: 0,
                      }}
                    >
                      <img
                        src={getAssetUrl(selectedAsset())}
                        alt={getAssetLabel(selectedAsset())}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: '#f5f5ff',
                          marginBottom: 4,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {getAssetLabel(selectedAsset())}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <button
                          type="button"
                          onClick={() => setLockEdit(!lockEdit)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '6px 10px',
                            borderRadius: 10,
                            border: '1px solid #3a3d52',
                            background: lockEdit ? '#262b3f' : '#191b26',
                            color: '#d0d3ea',
                            fontSize: 13,
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                          title={lockEdit ? 'Unlock overlay editing' : 'Lock overlay editing'}
                        >
                          {lockEdit ? <Lock size={16} /> : <LockOpen size={16} />}
                          {lockEdit ? 'Locked' : 'Unlocked'}
                        </button>
                        <div style={{ marginLeft: 'auto' }}>
                          <button
                            type="button"
                            onClick={() => {
                              if (typeof onStartEdit === 'function') {
                                onStartEdit(selectedAsset(), lockEdit);
                              }
                              onClose();
                            }}
                            style={{
                              padding: '8px 16px',
                              borderRadius: 10,
                              border: 'none',
                              background: 'linear-gradient(135deg, #3c6df6, #5f9dfd)',
                              color: '#fff',
                              fontSize: 13,
                              fontWeight: 700,
                              cursor: 'pointer',
                            }}
                          >
                            Edit on map
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: '#9ea3c0' }}>
                    Clicking <b>Edit on map</b> will close this window and enable drag/resize
                    on the overlay with a Save/Cancel bar above the map controls.
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '6px 12px',
              borderRadius: 999,
              border: '1px solid #3a3d52',
              background: '#191b26',
              color: '#d0d3ea',
              fontSize: 13,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Close
          </button>
          <button
            type="button"
            onClick={handleUseSelected}
            disabled={!selectedId}
            style={{
              padding: '6px 16px',
              borderRadius: 999,
              border: 'none',
              background: selectedId
                ? 'linear-gradient(135deg, #3c6df6, #5f9dfd)'
                : 'linear-gradient(135deg, #3a476f, #4a567d)',
              color: '#fff',
              fontSize: 13,
              fontWeight: 600,
              cursor: selectedId ? 'pointer' : 'default',
              opacity: selectedId ? 1 : 0.7,
            }}
          >
            Use selected overlay
          </button>
        </div>
      </div>
    </div>
  );
}

export default OverlayAssetModal;
